import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormHelperText
} from '@mui/material';
import { useTireDamageDataList } from '../../../../stores/tireDamageStore';
import { createTireInspection } from '../../../../stores/tireInspectionStore';
import { revalidateTireBatchList } from '../../../../stores/tireBatchStore';
import { useNotifier } from '../../../../components/Notifier';
import dayjs from 'dayjs';
import PickerFieldTable from '../../../../components/PickerFieldTable';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import { useLanguage } from '../../../../utils/i18n';

const buildReasonArgs = (state) => [state.page, state.pageSize, [], state.searchKey, state.searchValue];
const formatReasonOption = (r) => ({ value: r.id, label: r.name, original: r });

export default function TabTireBulkInspection({ selectedTires, onBack, vehicleDetails, companyId, hmUnit, kmUnit, date, refetch }) {
  const notify = useNotifier();
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const damageColumns = useMemo(
    () => [
      {
        field: 'code',
        headerName: t('general.code', 'Code'),
        width: 120,
        renderCell: (row) => row?.original?.code_internal || row?.original?.code || '-'
      },
      { field: 'name', headerName: t('general.name', 'Name'), width: 180, renderCell: (row) => row?.original?.name || '-' },
      { field: 'area', headerName: t('tireCycle.area', 'Area'), width: 120, renderCell: (row) => row?.original?.area || '-' },
      { field: 'cause', headerName: t('tireCycle.cause', 'Cause'), width: 120, renderCell: (row) => row?.original?.cause || '-' },
      {
        field: 'recommend_action',
        headerName: t('tireCycle.action', 'Action'),
        width: 120,
        renderCell: (row) => row?.original?.recommend_action || '-'
      },
      {
        field: 'description',
        headerName: t('general.description', 'Description'),
        width: 250,
        renderCell: (row) => row?.original?.description || '-'
      }
    ],
    [t]
  );

  const isAbnormalKmHm = useMemo(() => {
    if (kmUnit && hmUnit && Number(hmUnit) > 0) {
      const ratio = Number(kmUnit) / Number(hmUnit);
      if (ratio > 100 || ratio < 1) return true;
    }
    return false;
  }, [kmUnit, hmUnit]);

  const reasonPickerParams = usePickerPagination({
    fetchHook: useTireDamageDataList,
    buildArgs: buildReasonArgs,
    formatOption: formatReasonOption
  });
  const [bulkData, setBulkData] = useState(
    selectedTires.map((tire) => {
      const otd = tire.otd || 0;
      const ap = tire.lastInspection?.air_pressure || '';
      const td = tire.lastInspection?.tread_depth || otd || '';

      return {
        id: tire.id,
        tire_item: tire.tire_item,
        tire_item_id: tire.tire_item_id,
        posCode: tire.posCode,
        serial_number: tire.serial_number,
        position: tire.posLabel || tire.posCode,
        otd: otd,
        tread_depth: td,
        air_pressure: ap,
        condition: 'Good',
        tire_damage_id: '',
        status_backlog: '',
        inspector_name: '',
        notes: ''
      };
    })
  );

  const handleFieldChange = (id, field, value) => {
    setBulkData((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === 'condition' && value !== 'Damaged') {
            updated.tire_damage_id = '';
            updated.status_backlog = '';
          }
          return updated;
        }
        return item;
      })
    );
  };

  const handleBulkSubmit = () => {
    setShowErrors(true);
    // Basic validation
    for (const item of bulkData) {
      if (!item.tread_depth && item.tread_depth !== 0) {
        notify({
          message: `${item.serial_number}: ${t('tireCycle.treadDepthRequired', 'Tread depth is required')}`,
          severity: 'error'
        });
        return;
      }
      if (item.otd > 0 && parseFloat(item.tread_depth) > item.otd) {
        notify({
          message: `${item.serial_number}: ${t('tireCycle.treadDepthExceedsMax', 'Tread depth exceeds Max OTD')} (${item.otd} mm)`,
          severity: 'error'
        });
        return;
      }
      if (!item.air_pressure && item.air_pressure !== 0) {
        notify({
          message: `${item.serial_number}: ${t('tireCycle.airPressureRequired', 'Air pressure is required')}`,
          severity: 'error'
        });
        return;
      }
      if (!item.inspector_name) {
        notify({
          message: `${item.serial_number}: ${t('tireCycle.inspectorNameRequired', 'Mechanic / Inspector Name is required')}`,
          severity: 'error'
        });
        return;
      }
      if (item.condition === 'Damaged') {
        if (!item.tire_damage_id) {
          notify({
            message: `${item.serial_number}: ${t('tireCycle.damageRequired', 'Damage is required when Condition is Damaged')}`,
            severity: 'error'
          });
          return;
        }
        if (!item.status_backlog) {
          notify({
            message: `${item.serial_number}: ${t('tireCycle.priorityRequired', 'Priority is required when Condition is Damaged')}`,
            severity: 'error'
          });
          return;
        }
      }
    }

    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    let successCount = 0;

    for (const item of bulkData) {
      const payload = {
        company: parseInt(companyId) || vehicleDetails?.company_id || vehicleDetails?.company || 0,
        vehicle: vehicleDetails?.id || 0,
        position: item.posCode || item.position || '',
        tire_item: item.tire_item || item.tire_item_id || 0,
        serial_number: item.serial_number || '',
        inspection_date: date || dayjs().format('YYYY-MM-DD'),
        tread_depth: parseFloat(item.tread_depth) || 0,
        air_pressure: parseFloat(item.air_pressure) || 0,
        condition: item.condition,
        tire_damage: item.tire_damage_id || null,
        tire_damage_name: reasonPickerParams.options.find((o) => String(o.value) === String(item.tire_damage_id))?.label || '',
        notes: item.notes || '',
        status_backlog: item.status_backlog || '',
        inspector_name: item.inspector_name,
        km_unit: parseInt(kmUnit) || 0,
        hm_unit: parseInt(hmUnit) || 0
      };

      try {
        await createTireInspection(payload);
        successCount++;
      } catch (err) {
        notify({
          message: `${t('tireCycle.failedToInspect', 'Failed to inspect')} ${item.serial_number}: ${err.response?.data?.message || err.message}`,
          severity: 'error'
        });
      }
    }

    if (successCount > 0) {
      notify({
        message: `${t('tireCycle.successfullyInspected', 'Successfully inspected')} ${successCount} ${t('tireCycle.tires', 'tires')}`,
        severity: 'success'
      });
      revalidateTireBatchList();
      if (refetch) refetch();
      onBack();
    }
    setIsSubmitting(false);
    setConfirmOpen(false);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">{t('tireCycle.bulkInspectTires', 'Bulk Inspect Tires')}</Typography>
        <Button variant="outlined" onClick={onBack}>
          {t('tireCycle.backToList', 'Back to List')}
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: 1400, '& .MuiTableCell-root': { px: 1, py: 1.5 } }}>
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell sx={{ minWidth: 120 }}>{t('tireCardHistory.serialNumber', 'Serial Number')}</TableCell>
              <TableCell sx={{ minWidth: 80 }}>{t('tireCycle.position', 'Position')}</TableCell>
              <TableCell sx={{ minWidth: 120 }}>{t('general.mechanicName', 'Mechanic Name')}</TableCell>
              <TableCell sx={{ minWidth: 140 }}>{t('tireCycle.treadDepthMm', 'Tread Depth (mm)')}</TableCell>
              <TableCell sx={{ minWidth: 130 }}>{t('tireCycle.pressurePsi', 'Pressure (psi)')}</TableCell>
              <TableCell sx={{ minWidth: 120 }}>{t('general.condition', 'Condition')}</TableCell>
              <TableCell sx={{ minWidth: 160 }}>{t('tireCycle.damage', 'Damage')}</TableCell>
              <TableCell sx={{ minWidth: 130 }}>{t('tireCycle.priority', 'Priority')}</TableCell>
              <TableCell sx={{ minWidth: 150 }}>{t('tireCycle.notes', 'Notes')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bulkData.map((row) => {
              const isTdExceed = row.otd > 0 && parseFloat(row.tread_depth) > row.otd;
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    <Typography fontWeight="bold">{row.serial_number}</Typography>
                  </TableCell>
                  <TableCell>{row.position}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.inspector_name}
                      onChange={(e) => handleFieldChange(row.id, 'inspector_name', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      value={row.tread_depth}
                      onChange={(e) => handleFieldChange(row.id, 'tread_depth', e.target.value)}
                      inputProps={{ min: 0 }}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === 'e') e.preventDefault();
                      }}
                      error={isTdExceed}
                      helperText={isTdExceed ? `${t('tireCycle.maxOtd', 'Max OTD')}: ${row.otd}` : ''}
                      FormHelperTextProps={{ sx: { mx: 0, mt: 0.5, fontSize: '0.65rem' } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      value={row.air_pressure}
                      onChange={(e) => handleFieldChange(row.id, 'air_pressure', e.target.value)}
                      inputProps={{ min: 0 }}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === 'e') e.preventDefault();
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select value={row.condition} onChange={(e) => handleFieldChange(row.id, 'condition', e.target.value)}>
                        <MenuItem value="Good">{t('general.good', 'Good')}</MenuItem>
                        <MenuItem value="Fair">{t('general.fair', 'Fair')}</MenuItem>
                        <MenuItem value="Damaged">{t('general.damaged', 'Damaged')}</MenuItem>
                        <MenuItem value="Poor">{t('general.poor', 'Poor')}</MenuItem>
                        <MenuItem value="Scrap">{t('general.scrap', 'Scrap')}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <PickerFieldTable
                      multiSelect={false}
                      {...reasonPickerParams}
                      options={reasonPickerParams.options}
                      columns={damageColumns}
                      value={row.tire_damage_id}
                      onChange={(val) => handleFieldChange(row.id, 'tire_damage_id', val)}
                      selectedItems={
                        row.tire_damage_id ? reasonPickerParams.options.filter((o) => String(o.value) === String(row.tire_damage_id)) : []
                      }
                      getOptionId={(o) => o?.value}
                      getOptionLabel={(o) => o?.label}
                      dialogTitle={t('tireCycle.selectTireDamage', 'Select Tire Damage')}
                      dialogProps={{ maxWidth: 'lg' }}
                      disabled={row.condition !== 'Damaged'}
                      size="small"
                      placeholder={t('tireCycle.selectDamage', 'Select Damage')}
                      error={showErrors && row.condition === 'Damaged' && !row.tire_damage_id}
                      helperText={showErrors && row.condition === 'Damaged' && !row.tire_damage_id ? t('general.required', 'Required') : ''}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl
                      size="small"
                      fullWidth
                      disabled={row.condition !== 'Damaged'}
                      error={showErrors && row.condition === 'Damaged' && !row.status_backlog}
                    >
                      <Select
                        value={row.status_backlog}
                        onChange={(e) => handleFieldChange(row.id, 'status_backlog', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>{t('tireCycle.none', 'None')}</em>
                        </MenuItem>
                        <MenuItem value="LOW">{t('general.low', 'LOW')}</MenuItem>
                        <MenuItem value="MEDIUM">{t('general.medium', 'MEDIUM')}</MenuItem>
                        <MenuItem value="HIGH">{t('general.high', 'HIGH')}</MenuItem>
                        <MenuItem value="CRITICAL">{t('general.critical', 'CRITICAL')}</MenuItem>
                      </Select>
                      {showErrors && row.condition === 'Damaged' && !row.status_backlog && (
                        <FormHelperText>{t('general.required', 'Required')}</FormHelperText>
                      )}
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.notes}
                      onChange={(e) => handleFieldChange(row.id, 'notes', e.target.value)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" color="primary" size="large" onClick={handleBulkSubmit}>
          {t('tireCycle.confirmAllInspections', 'Confirm All Inspections')}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => !isSubmitting && setConfirmOpen(false)}>
        <DialogTitle>{t('tireCycle.confirmBulkInspection', 'Confirm Bulk Inspection')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`${t('tireCycle.areYouSureSubmitInspections', 'Are you sure you want to submit inspections for')} ${bulkData.length} ${t('tireCycle.tiresQuestion', 'tires?')}`}
          </DialogContentText>
          {isAbnormalKmHm && (
            <Typography color="error" variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
              {t(
                'tireCycle.abnormalKmHmWarning',
                'Warning: The inputted KM and HM values appear to be abnormal. Please verify the data is correct before proceeding.'
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>
            {t('general.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirmSubmit} variant="contained" color="primary" disabled={isSubmitting}>
            {isSubmitting ? t('general.submitting', 'Submitting...') : t('general.confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
